import { yupResolver } from "@hookform/resolvers/yup";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import api from "../api";
import SocialSignInButtons from "../components/SocialSignInButtons";
import { persistPortalSession } from "../utils/portalSession";
import { button, icon } from "./style";
// Student portal login: username and password only. No role fetch — backend logs you in and returns your role (student → /student/).
const schema = yup.object().shape({
  email: yup.string().required("Username is required").trim(),
  password: yup.string().required("Password is required"),
});

const Login = () => {
  const navigate = useNavigate();

  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "", // sent as "email" to API; backend treats it as login identifier (username)
      password: "",
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("email", data.email);
    formData.append("password", data.password);
    // Omit choose_the_role: backend accepts username+password and returns the user's role (student → student portal).

    try {
      setLoginLoading(true);
      const res = await api.post('login', formData);
      const responseData = res.data.data;
      const path = persistPortalSession(responseData);
      // Tester accounts must use the Admin login form (Admin Passport scope required).
      if (!path) {
        toast.info("Tester accounts must log in via the Admin login page.");
        navigate("/admin-login");
        return;
      }
      toast.success("Login successful!");
      navigate(path);

    } catch (error) {
      const errDetail = error.response?.data?.data?.error;
      const errMsg =
        (typeof errDetail === "string" && errDetail) ||
        error.response?.data?.message ||
        "Login failed!";
      toast.error(errMsg);
      if (
        error.response?.status === 403 &&
        typeof errDetail === "string" &&
        errDetail.toLowerCase().includes("tutor")
      ) {
        navigate("/tutor-login");
      }
    } finally {
      setLoginLoading(false);
    }

  };

  return (
    <>
      <Grid container sx={{ height: "100vh" }}>

        {/* Left Section */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundImage: `url("/assets/images/signup-bg.png")`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            // minHeight: { xs: 200, md: "auto" },

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            py: 9
          }}
        >
          <img
            src="/assets/images/logo.optimized.svg"
            alt="Logo"
            style={{ width: "80%", maxWidth: "320px", height: "auto" }}
          />
        </Grid>

        {/* Right - Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: "#fff",
            py: 6,
            px: { xs: 4, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{ width: "100%", maxWidth: 400 }}
          >

            {/* Heading */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              <Box component="span" sx={{ color: "#D6232A" }}>Login</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Log in with Google, Apple, or your username. You can switch to the parent portal with your PIN after logging in.
            </Typography>
            <SocialSignInButtons />
            {/* Username */}
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Username"
                  placeholder="Enter username"
                  variant="outlined"
                  {...field}
                  value={field.value ?? ""}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ mb: 2 }}
                  disabled={loginLoading}
                />
              )}
            />
            {/* Password */}
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  variant="outlined"
                  {...field}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  sx={{ mb: 2 }}
                  disabled={loginLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          sx={{ color: "#666" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              <Link to="/forgot-password" style={{ color: "#1565c0", fontWeight: 600 }}>Forgot password?</Link>
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-start", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
              <Button
                disableElevation
                sx={{
                  ...button,
                  backgroundColor: "#EF2A1E",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#EF2A1E",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#EF2A1E",
                    color: "#fff",
                    opacity: 0.7,
                  },
                }}
                onClick={handleSubmit(onSubmit)}
                disabled={loginLoading}
              >
                {loginLoading ? "Logging in..." : "Login"}
                {!loginLoading && (
                  <Box sx={icon}>
                    <ArrowForwardIcon sx={{ fontSize: 20, color: "#EF2A1E" }} />
                  </Box>
                )}
              </Button>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Are you a tutor?{" "}
                <Link to="/tutor-login" style={{ color: "#1565c0", fontWeight: 600 }}>Log in to the tutor panel</Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default Login;
